import React, { memo, useState, useCallback, useEffect, UIEvent } from 'react';
import {
  SideNavCategory,
  SideNavColorSchemeProps,
  SideNavItemSelectEvent,
} from '..';
import SubCategorySection from '../sub-category-section/SubCategorySection';
import { escapeRegExp, removeDoubleSpaces } from '../../../util';
import * as GStyled from '../../../styles/components';
import * as Styled from './styled';
import { StringOrComponent } from '../../../models';
import { useRouter } from 'next/router';
import { LoadingSkeleton } from '../..';
import RefreshPanel from '../refresh-panel/RefreshPanel';
import { useIsFrs102Enabled } from '../../../util/hooks/useIsFrs102Enabled';
import { replaceIfrsLabels } from '../../../util/frs102Helper';

interface SubCategoryPanelProps extends SideNavColorSchemeProps {
  category: SideNavCategory;
  setIsSearching: (boolean) => void;
  categoriesToSearch?: Array<SideNavCategory>;
  onItemSelect?: (e: SideNavItemSelectEvent) => void;
  onScroll?: (e: UIEvent<HTMLElement>) => void;
  onItemDoubleClick?: () => void;
  selectedItem?: SideNavCategory;
  isLoading?: boolean;
  isDraggable?: boolean;
  dropableId?: string;
  subItemDisplayBuilder?: (category: SideNavCategory) => StringOrComponent;
  subItemListBuilder?: (
    category: SideNavCategory,
    defaultItemComponents?: StringOrComponent[],
  ) => StringOrComponent;
  refreshFieldsCallback: () => void;
  canShowRefresh: boolean;
  onCloseRefreshPanel: () => void;
}

function filterSubCategories(
  subCategories: Array<SideNavCategory>,
  searchString: string,
  category: SideNavCategory,
  isFrs102Enabled: boolean,
): Array<SideNavCategory> {
  return subCategories.reduce((acc, subCategory) => {
    const items = subCategory.items.filter((item) => {
      const formattedSearchString = removeDoubleSpaces(searchString).trim();
      const formattedSourceString = removeDoubleSpaces(item.name).trim();
      const ifrsFormattedSourceString = replaceIfrsLabels(
        formattedSourceString,
        isFrs102Enabled,
      );
      const patt = new RegExp(escapeRegExp(formattedSearchString), 'gi');
      return patt.test(ifrsFormattedSourceString);
    });
    // if MyFields or My Reports,then display category names even when zero items;
    // ignore when searching
    if (
      (category.key === 'MyFields' ||
        category.key === 'My Reports' ||
        category.key === 'Shared With Me') &&
      !searchString
    ) {
      acc.push({ ...subCategory, items });
    } else if (items && items.length) {
      acc.push({ ...subCategory, items });
    }
    return acc;
  }, []);
}

function SubCategoryPanel({
  category,
  setIsSearching,
  categoriesToSearch,
  onItemSelect,
  onScroll,
  selectedItem,
  colorScheme,
  onItemDoubleClick,
  isLoading,
  isDraggable,
  dropableId,
  subItemDisplayBuilder,
  subItemListBuilder,
  refreshFieldsCallback,
  canShowRefresh,
  onCloseRefreshPanel
}: SubCategoryPanelProps): JSX.Element {
  const router = useRouter();
  const categoryItems = category?.items;
  const [searchString, setSearchString] = useState('');
  const [subCategories, setSubCategories] = useState(categoryItems);
  const [showRefresh, setShowRefresh] = useState(false);
  const reportOrFieldsText =
    router.pathname === '/create' ? 'Fields' : 'Reports';
  const isFrs102Enabled = useIsFrs102Enabled();

  useEffect(
    () => {
      setTimeout(() => setShowRefresh(canShowRefresh), 6000);
    }, 
    [canShowRefresh]);
  
  useEffect(() => {
    const categoryItemsToFilter = !!searchString
      ? categoriesToSearch
      : categoryItems;

    const filteredSubCategories = filterSubCategories(
      categoryItemsToFilter || [],
      searchString,
      category,
      isFrs102Enabled,
    );

    setSubCategories(filteredSubCategories);
  }, [categoryItems, searchString, category, categoriesToSearch, isFrs102Enabled]);

  const searchInputChangeHandler = useCallback(
    (e): void => {
      const inputedValue = e.target.value;
      setSearchString(inputedValue);
      setIsSearching(!!inputedValue);      
    },
    [setIsSearching],
  );

  useEffect(() => {
    setSearchString('');
    setIsSearching(false);
  }, [category?.name, setIsSearching]);

  const refreshFields = () => {
    setShowRefresh(false);
    refreshFieldsCallback();
  };

  return (
    <Styled.SubCategoryPanel colorScheme={colorScheme}>
      <Styled.SearchBox
        placeholder={`Search All ${reportOrFieldsText}`}
        value={searchString}
        onChange={searchInputChangeHandler}
        variant="search"
        name="findReport-filterSection-searchSubCategory"
      />
      {
        showRefresh ? 
        (
          <RefreshPanel 
            onClose={ () => {setShowRefresh(false); onCloseRefreshPanel();} } 
            onRefresh={ () => { refreshFields(); } }  
          />
        ) 
        : 
        null
      }
      
      <GStyled.ScrollableContainer overFlowX="hidden" onScroll={onScroll}>
        {!isLoading ? (
          subCategories && subCategories.length > 0 ? (
            subCategories.map((subCategory) => (
              <SubCategorySection
                key={subCategory.key}
                subCategory={subCategory}
                onItemSelect={onItemSelect}
                selectedItem={selectedItem}
                colorScheme={colorScheme}
                onItemDoubleClick={onItemDoubleClick}
                isCollapsible={subCategories.length > 1}
                isDraggable={isDraggable}
                dropableId={`${dropableId}-${subCategory.key}`}
                subItemDisplayBuilder={subItemDisplayBuilder}
                subItemListBuilder={subItemListBuilder}
                isSearching={!!searchString}
              />
            ))
          ) : (
            <Styled.NoResult colorScheme={colorScheme}>
              No results found
            </Styled.NoResult>
          )
        ) : (
          <LoadingSkeleton name="list" />
        )}
      </GStyled.ScrollableContainer>
    </Styled.SubCategoryPanel>
  );
}

SubCategoryPanel.defaultProps = {
  colorScheme: 'dark',
  isLoading: false,
  category: {
    items: [],
  },
};

export default memo(SubCategoryPanel);
